/* Pure helpers shared by the Inbox panes. No React here. */

export function safeHostname(url) {
  try { return new URL(url).hostname.replace(/^www\./, ''); } catch { return ''; }
}

// Strip HTML tags (complete or truncated), decode a handful of common entities,
// and collapse whitespace. LLM descriptions sometimes echo raw markup from the
// source article — render plain text only.
export function cleanText(value) {
  if (!value) return '';
  let s = String(value);
  // Drop complete tags first, then any dangling opening tag (truncated output).
  s = s.replace(/<\/?[a-zA-Z][^>]*>/g, '');
  s = s.replace(/<[^>]*$/g, '');
  s = s
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'");
  return s.replace(/\s+/g, ' ').trim();
}

/** Same as cleanText but keeps line breaks, for markdown input. */
export function cleanMarkdown(value) {
  if (!value) return '';
  return String(value)
    .replace(/<\/?[a-zA-Z][^>]*>/g, '')
    .replace(/<[^>]*$/g, '')
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'");
}

/** Compact age for list rows: "now", "12m", "5h", "3d", then a UTC date. */
export function relTime(ts) {
  if (!ts) return '';
  const d = new Date(ts);
  if (Number.isNaN(d.getTime())) return '';
  const m = Math.floor((Date.now() - d.getTime()) / 60000);
  if (m < 1) return 'now';
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h`;
  const days = Math.floor(h / 24);
  if (days < 7) return `${days}d`;
  return d.toISOString().slice(0, 10);
}

/** SOC timelines are kept in UTC: "2026-10-15 06:05 UTC". */
export function formatUtc(ts) {
  if (!ts) return '';
  const d = new Date(ts);
  if (Number.isNaN(d.getTime())) return '';
  return `${d.toISOString().slice(0, 16).replace('T', ' ')} UTC`;
}

export function formatLocal(ts) {
  if (!ts) return '';
  const d = new Date(ts);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleString();
}

/** One short ID format everywhere: first 8 hex chars of the attack id. */
export function shortId(id) {
  return String(id || '').replace(/-/g, '').slice(0, 8).toUpperCase();
}

export const briefId = (b) => (b ? String(b.attack_id || b.id || '') : '');

/** Free-text match used by the queue filter. */
export function matchesQuery(b, q) {
  if (!q) return true;
  const hay = [
    b.name,
    b.description,
    b.threat_actor,
    b.insight,
    ...(b.threat_actor_aliases || []),
    ...(b.iocs || []),
    ...(b.cves || []),
    ...(b.ttps || []),
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
  return hay.includes(q);
}

/* ------------------------------------------------------------
   Insight: split the "Recommended actions" section out of the LLM
   insight so actions are shown once (in Mitigations), not twice.
   ------------------------------------------------------------ */
const ACTIONS_HEADING = /^\s*(?:#{1,4}\s*)?\**\s*recommended (?:actions|mitigations)\s*\**:?\s*$/i;
const ANY_HEADING = /^\s*(?:#{1,4}\s+.+|\*\*[^*]+\*\*:?)\s*$/;
const LIST_ITEM = /^\s*(?:[-*•]|\d+[.)])\s+(.*)$/;

export function splitInsight(text) {
  const lines = cleanMarkdown(text).replace(/\r\n?/g, '\n').split('\n');
  const body = [];
  const actions = [];
  let inActions = false;
  lines.forEach((line) => {
    if (ACTIONS_HEADING.test(line)) { inActions = true; return; }
    if (inActions && ANY_HEADING.test(line)) inActions = false;
    if (!inActions) { body.push(line); return; }
    const item = line.match(LIST_ITEM);
    if (item) actions.push(item[1].trim());
    else if (line.trim() && actions.length) actions[actions.length - 1] += ` ${line.trim()}`;
  });
  return { body: body.join('\n').trim(), actions };
}

/** CVSS base score quoted by the sources, if the insight states one. */
export function extractCvss(text) {
  const m = String(text || '').match(/CVSS[^0-9\n]{0,40}(\d{1,2}(?:\.\d)?)/i);
  if (!m) return null;
  const score = parseFloat(m[1]);
  return score >= 0 && score <= 10 ? score : null;
}

/** CVSS v3/v4 qualitative band. */
export function cvssBand(score) {
  if (score == null) return null;
  if (score >= 9) return 'Critical';
  if (score >= 7) return 'High';
  if (score >= 4) return 'Medium';
  if (score > 0) return 'Low';
  return 'Info';
}

/* Backend mitigations carry placeholder titles ("Mitigation Step 3"). */
export function mitigationTitle(m) {
  const t = cleanText(m?.title);
  return /^mitigation(?: step)? \d+$/i.test(t) ? '' : t;
}

/* ------------------------------------------------------------
   IOCs: classify, defang for display, keep the raw value for copy.
   ------------------------------------------------------------ */
export const IOC_TYPES = [
  { key: 'ipv4',   label: 'IPv4' },
  { key: 'ipv6',   label: 'IPv6' },
  { key: 'domain', label: 'Domain' },
  { key: 'url',    label: 'URL' },
  { key: 'email',  label: 'Email' },
  { key: 'sha256', label: 'SHA-256' },
  { key: 'sha1',   label: 'SHA-1' },
  { key: 'md5',    label: 'MD5' },
  { key: 'cve',    label: 'CVE' },
  { key: 'other',  label: 'Other' },
];

// Refang common defanged forms so classification and copy use the real value.
export function refang(value) {
  return String(value || '')
    .trim()
    .replace(/^hxxp/i, 'http')
    .replace(/\[\.\]|\(\.\)|\{\.\}|\[dot\]/gi, '.')
    .replace(/\[:\]/g, ':')
    .replace(/\[@\]|\[at\]/gi, '@');
}

export function classifyIoc(raw) {
  const v = refang(raw);
  if (/^CVE-\d{4}-\d{4,7}$/i.test(v)) return 'cve';
  if (/^[a-f0-9]{64}$/i.test(v)) return 'sha256';
  if (/^[a-f0-9]{40}$/i.test(v)) return 'sha1';
  if (/^[a-f0-9]{32}$/i.test(v)) return 'md5';
  if (/^(?:25[0-5]|2[0-4]\d|1?\d?\d)(?:\.(?:25[0-5]|2[0-4]\d|1?\d?\d)){3}(?::\d{1,5})?(?:\/\d{1,2})?$/.test(v)) return 'ipv4';
  if ((/^[0-9a-f:]+$/i.test(v) && v.includes('::')) || /^(?:[0-9a-f]{1,4}:){7}[0-9a-f]{1,4}$/i.test(v)) return 'ipv6';
  if (/^[a-z][a-z0-9+.-]*:\/\/\S+$/i.test(v)) return 'url';
  if (/^[^\s@]+@[a-z0-9.-]+\.[a-z]{2,}$/i.test(v)) return 'email';
  if (/^(?=.{4,253}$)(?:[a-z0-9_](?:[a-z0-9_-]{0,61}[a-z0-9])?\.)+[a-z]{2,24}$/i.test(v)) return 'domain';
  return 'other';
}

/** Display form that will not auto-link or resolve if pasted into chat or mail. */
export function defang(value, type) {
  const v = refang(value);
  if (type === 'url') return v.replace(/^http/i, 'hxxp').replace(/\./g, '[.]');
  if (type === 'domain' || type === 'ipv4') return v.replace(/\./g, '[.]');
  if (type === 'email') return v.replace('@', '[@]').replace(/\./g, '[.]');
  if (type === 'ipv6') return v.replace(/:/g, '[:]');
  return v;
}

/** [{ type, label, items: [{ raw, value, display }] }] in IOC_TYPES order, empty groups dropped. */
export function groupIocs(iocs) {
  const groups = new Map(IOC_TYPES.map((t) => [t.key, { type: t.key, label: t.label, items: [] }]));
  const seen = new Set();
  (iocs || []).forEach((raw) => {
    const value = refang(raw);
    if (!value || seen.has(value.toLowerCase())) return;
    seen.add(value.toLowerCase());
    const type = classifyIoc(value);
    groups.get(type).items.push({ raw, value: type === 'other' ? String(raw).trim() : value, display: defang(value, type) });
  });
  return [...groups.values()].filter((g) => g.items.length);
}

export async function copyText(text) {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(text);
    return;
  }
  // Fallback for non-secure contexts.
  const ta = document.createElement('textarea');
  ta.value = text;
  ta.setAttribute('readonly', '');
  ta.style.position = 'fixed';
  ta.style.opacity = '0';
  document.body.appendChild(ta);
  ta.select();
  const ok = document.execCommand('copy');
  ta.remove();
  if (!ok) throw new Error('Copy failed');
}

export function downloadBlob(data, filename, type = 'text/plain;charset=utf-8') {
  const blob = data instanceof Blob ? data : new Blob([data], { type });
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
}
